import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getEvents, IEventItem } from "../../services/eventsApi";
import { RootState } from "../../store/store";
import CreateEventModal from "./CreateEventModal";

const statusFilters = [
  "All",
  "Ongoing",
  "Upcoming",
  "Completed",
  "Canceled",
  "Draft",
] as const;
type StatusFilter = (typeof statusFilters)[number];

const eventState = (event: IEventItem) => {
  const status = String(event.status || "").toLowerCase();
  if (status === "canceled" || status === "cancelled") return "Canceled";
  if (status === "completed") return "Completed";
  if (status === "draft") return "Draft";
  const now = Date.now();
  const start = new Date(event.startDateTime).getTime();
  const end = new Date(event.endDateTime || event.startDateTime).getTime();
  if (start <= now && end >= now) return "Ongoing";
  if (start > now) return "Upcoming";
  return "Completed";
};

const statusStyle: Record<string, string> = {
  Ongoing: "bg-emerald-100 text-emerald-700",
  Upcoming: "bg-blue-100 text-blue-700",
  Completed: "bg-slate-100 text-slate-600",
  Canceled: "bg-red-100 text-red-700",
  Draft: "bg-amber-100 text-amber-700",
};

const sortEventsByRelevance = (items: IEventItem[]) => {
  const now = Date.now();
  const rank = (event: IEventItem) => {
    if (eventState(event) === "Canceled") return 3;
    const start = new Date(event.startDateTime).getTime();
    const end = new Date(event.endDateTime || event.startDateTime).getTime();
    if (start <= now && end >= now) return 0;
    if (start > now) return 1;
    return 2;
  };
  return [...items].sort((a, b) => {
    const aRank = rank(a);
    const bRank = rank(b);
    if (aRank !== bRank) return aRank - bRank;
    const aStart = new Date(a.startDateTime).getTime();
    const bStart = new Date(b.startDateTime).getTime();
    return aRank >= 2 ? bStart - aStart : aStart - bStart;
  });
};

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || error?.message || fallback;

export default function EventsPage() {
  const navigate = useNavigate();
  const userId = useSelector((state: RootState) => state.auth.user?._id || "");
  const eventsRequestId = useRef(0);
  const [events, setEvents] = useState<IEventItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const loadEvents = useCallback(async () => {
    const requestId = ++eventsRequestId.current;
    try {
      setLoading(true);
      setLoadError("");
      const nextEvents = await getEvents();
      if (eventsRequestId.current !== requestId) return;
      setEvents(sortEventsByRelevance(nextEvents));
    } catch (error) {
      if (eventsRequestId.current !== requestId) return;
      setLoadError(errorMessage(error, "Could not load events."));
    } finally {
      if (eventsRequestId.current === requestId) setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setEvents([]);
    setSearch("");
    setStatusFilter("All");
    setDateFrom("");
    setDateTo("");
    setShowCreate(false);
    void loadEvents();
    return () => {
      eventsRequestId.current += 1;
    };
  }, [loadEvents]);

  const filteredEvents = useMemo(() => {
    const query = search.trim().toLowerCase();
    const fromTime = dateFrom
      ? new Date(`${dateFrom}T00:00:00`).getTime()
      : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : null;
    return events.filter((event) => {
      const eventStart = new Date(event.startDateTime).getTime();
      const eventEnd = new Date(
        event.endDateTime || event.startDateTime,
      ).getTime();
      const matchesStatus =
        statusFilter === "All" || eventState(event) === statusFilter;
      const matchesDate =
        (fromTime === null || eventEnd >= fromTime) &&
        (toTime === null || eventStart <= toTime);
      const matchesSearch =
        !query ||
        [event.title, event.location, event.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));
      return matchesStatus && matchesDate && matchesSearch;
    });
  }, [dateFrom, dateTo, events, search, statusFilter]);

  const filtersActive = Boolean(
    search || statusFilter !== "All" || dateFrom || dateTo,
  );

  return (
    <div className="min-h-[calc(100vh-56px)] bg-slate-50/90 px-4 py-8 md:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium text-blue-600">
              Event management
            </p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900">
              Events
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Plan events and keep their details in one place.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
          >
            + Create event
          </button>
        </div>

        <div className="mt-7 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by event name, location, or description"
            className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
          <div
            className="mt-4 flex gap-2 overflow-x-auto pb-1"
            aria-label="Filter events by status"
          >
            {statusFilters.map((status) => {
              const count =
                status === "All"
                  ? events.length
                  : events.filter((event) => eventState(event) === status)
                      .length;
              const selected = statusFilter === status;
              return (
                <button
                  key={status}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setStatusFilter(status)}
                  className={`shrink-0 rounded-full border px-3 py-1.5 text-sm font-semibold transition ${selected ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700"}`}
                >
                  {status}{" "}
                  <span
                    className={selected ? "text-blue-100" : "text-slate-400"}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-end">
            <label className="block flex-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              From date
              <input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(event) => setDateFrom(event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </label>
            <label className="block flex-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              To date
              <input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(event) => setDateTo(event.target.value)}
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal normal-case tracking-normal text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </label>
            {(dateFrom || dateTo) && (
              <button
                type="button"
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                }}
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Clear dates
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                className="h-48 animate-pulse rounded-xl border border-slate-200 bg-white"
              />
            ))}
          </div>
        ) : loadError ? (
          <div className="mt-5 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">
            {loadError}
            <button
              type="button"
              onClick={() => void loadEvents()}
              className="ml-3 font-semibold underline"
            >
              Retry
            </button>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="mt-5 rounded-xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <h2 className="text-lg font-semibold text-slate-900">
              {filtersActive ? "No matching events" : "No events yet"}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {filtersActive
                ? "Try a different search, status, or date filter."
                : "Create your first event to get started."}
            </p>
          </div>
        ) : (
          <div className="mt-5 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredEvents.map((event) => {
              const state = eventState(event);
              const starts = new Date(event.startDateTime);
              const ends = event.endDateTime
                ? new Date(event.endDateTime)
                : null;
              const formatDate = (value: Date | null) =>
                value && !Number.isNaN(value.getTime())
                  ? value.toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "Not set";
              return (
                <button
                  key={event._id}
                  type="button"
                  onClick={() => navigate(`/events/${event._id}`)}
                  className="group rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-300 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="line-clamp-2 text-lg font-semibold text-slate-900 group-hover:text-blue-700">
                      {event.title}
                    </h2>
                    <span
                      className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyle[state]}`}
                    >
                      {state}
                    </span>
                  </div>
                  <div className="mt-5 space-y-2 text-sm text-slate-600">
                    <p>
                      <span className="font-medium text-slate-800">Date:</span>{" "}
                      {formatDate(starts)} - {formatDate(ends)}
                    </p>
                    <p>
                      <span className="font-medium text-slate-800">Time:</span>{" "}
                      {starts.toLocaleTimeString(undefined, {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </p>
                    <p className="truncate">
                      <span className="font-medium text-slate-800">
                        Location:
                      </span>{" "}
                      {event.location || "Not set"}
                    </p>
                  </div>
                  <div className="mt-5 border-t border-slate-100 pt-3 text-sm font-semibold text-blue-600">
                    View event details →
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <CreateEventModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={loadEvents}
      />
    </div>
  );
}
